import React, { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../../src/Config";
import "./DashboardFilters.css";

export default function DashboardFilters({
  filters,
  onFilterChange,
  onApply,
  onReset,
  showPermitType = false,
}) {
  const [departments, setDepartments] = useState([]);
  const [permitTypes, setPermitTypes] = useState([]);
  const [prevDates, setPrevDates] = useState({
    start_date: "",
    end_date: "",
  });
  const [loading, setLoading] = useState(false);

  const datePresets = [
    { label: "All", value: "all" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "Current Week", value: "current_week" },
    { label: "Current Month", value: "current_month" },
    { label: "Next 7 Days", value: "next7days" },
    { label: "Next Month", value: "next_month" },
    { label: "Previous Month", value: "previous_month" },
    { label: "Previous Week", value: "past_week" },
    { label: "Custom Range", value: "custom" },
  ];

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(`${API_URL}/api/get_departments`, {
        headers: { Authorization: authKey },
      });

      const data = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      setDepartments(data);
    } catch (error) {
      console.error("Error fetching department data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermitTypes = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(`${API_URL}/api/get_work_permit_types`, {
        headers: { Authorization: authKey },
      });

      const permitTypeOptions = response.data.work_permit_types.map(
        (permitType) => ({
          value: permitType.id,
          label: permitType.work_permit_name,
        })
      );

      setPermitTypes(permitTypeOptions);
    } catch (error) {
      console.error("Error fetching permit types:", error.message);
    }
  };

  useEffect(() => {
    fetchDepartments();
    if (showPermitType) {
      fetchPermitTypes();
    }
  }, [showPermitType]);

  // Handle filter change with auto-apply
  const handleFilterChange = (newFilters, skipApply = false) => {
    onFilterChange(newFilters);

    if (!skipApply) {
      onApply(newFilters);
    }
  };

  // Check if both custom dates are selected
  const shouldApplyCustomDateFilter = (newFilters) => {
    return newFilters.custom_from && newFilters.custom_to;
  };

  return (
    <div className="newdashboard-filters-wrapper">
      {/* Department Filter */}
      <div className="newdashboard-filter-group">
        <label className="newdashboard-filter-label">Department</label>
        <select
          className="newdashboard-select"
          value={filters.department_id}
          onChange={(e) =>
            handleFilterChange({ ...filters, department_id: e.target.value })
          }
          disabled={loading}
        >
          <option value="">All Departments</option>
          {departments.map((dept) => (
            <option key={dept.ID} value={dept.ID}>
              {dept.DepartmentName}
            </option>
          ))}
        </select>
      </div>

      {/* Permit Type Filter - Conditional */}
      {showPermitType && (
        <div className="newdashboard-filter-group">
          <label className="newdashboard-filter-label">Permit Type</label>
          <select
            className="newdashboard-select"
            value={filters.permit_type_id || ""}
            onChange={(e) =>
              handleFilterChange({ ...filters, permit_type_id: e.target.value })
            }
            disabled={loading}
          >
            <option value="">All Permit Types</option>
            {permitTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Date Range */}
      <div className="newdashboard-filter-group">
        <label className="newdashboard-filter-label">Date Range</label>
        <div className="newdashboard-date-wrapper">
          <select
            className="newdashboard-select newdashboard-select-flex"
            value={filters.date_preset}
            onChange={(e) => {
              const value = e.target.value;

              const newFilters = {
                ...filters,
                date_preset: value,
                showCustomDate: value === "custom",
                start_date: "",
                end_date: "",
              };

              // ❌ Custom range → DO NOT CALL API
              const skipApply = value === "custom";

              handleFilterChange(newFilters, skipApply);
            }}
          >
            {datePresets.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>

          {filters.showCustomDate && (
            <div className="newdashboard-custom-date-wrapper">

              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => {
                  const val = e.target.value;

                  // ❌ Ignore if user navigated month and did not pick a date
                  if (!val || val === prevDates.start_date) return;

                  setPrevDates((p) => ({ ...p, start_date: val }));

                  const newFilters = {
                    ...filters,
                    start_date: val,
                    end_date: "", // clear end date when from changes
                  };

                  // ❌ User picked FROM only → do not call API
                  handleFilterChange(newFilters, true);
                }}
                className="newdashboard-date-input"
              />

              <input
                type="date"
                value={filters.end_date}
                disabled={!filters.start_date}
                min={filters.start_date || undefined}
                onChange={(e) => {
                  const val = e.target.value;

                  // ❌ Ignore month navigation (value unchanged)
                  if (!val || val === prevDates.end_date) return;

                  setPrevDates((p) => ({ ...p, end_date: val }));

                  const newFilters = {
                    ...filters,
                    end_date: val,
                  };

                  // ✔ Only call API when BOTH dates selected
                  const skipApply = !(
                    newFilters.start_date && newFilters.end_date
                  );

                  handleFilterChange(newFilters, skipApply);
                }}
                className="newdashboard-date-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="newdashboard-filter-actions">
        <button
          className="newdashboard-btn newdashboard-btn-outline"
          onClick={() => {
            onReset();
            // Apply filters after reset to show all data
            setTimeout(() => {
              onApply({});
            }, 100);
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
